import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import { createHead } from '@vueuse/head'
import App from './App.vue'
import { routes } from './router'
import { i18n } from './i18n'
import { initTheme } from '@shared/composables/useDarkMode'
import { useAuth } from '@shared/composables/useAuth'
import { prefetchCriticalRoutes } from '@shared/composables/usePrefetch'
import '@styles/main.css'

const RELOAD_KEY = '__unmoored_chunk_reload_count'
const MAX_RELOADS = 2

export const createApp = ViteSSG(App, { routes }, ({ app, router, initialState }) => {
  const pinia = createPinia()
  const head = createHead()

  app.use(pinia)
  app.use(head)
  app.use(i18n)

  initTheme()

  // Global error handler: prevents unhandled errors from crashing the app
  app.config.errorHandler = (err, instance, info) => {
    console.error('[App Error]', info, err)
  }

  // Router error handler: catch chunk load failures and auto-retry (with limit)
  router.onError((error, to) => {
    const isChunkError = error instanceof TypeError
      || error.message.includes('Failed to fetch dynamically imported module')
      || error.message.includes('ChunkLoadError')
      || error.message.includes('Loading chunk')
      || error.message.includes('Loading CSS chunk')

    if (isChunkError && typeof window !== 'undefined') {
      const count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10)
      if (count < MAX_RELOADS) {
        sessionStorage.setItem(RELOAD_KEY, String(count + 1))
        console.warn(`[Router] Chunk load failed, reloading (${count + 1}/${MAX_RELOADS}):`, to.fullPath)
        window.location.href = to.fullPath
      } else {
        console.error('[Router] Chunk load failed after max retries, giving up:', to.fullPath, error)
        sessionStorage.removeItem(RELOAD_KEY)
      }
    } else if (!isChunkError) {
      console.error('[Router] Navigation error:', error)
    }
  })

  // Clear reload counter on successful navigation
  router.afterEach(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(RELOAD_KEY)
    }
  })

  const { currentUser } = useAuth()
  const publicRoutes = new Set(['login', 'travel', 'travel-footprint', 'travel-detail'])
  router.beforeEach((to) => {
    if (!publicRoutes.has(to.name as string) && !currentUser.value) {
      return { name: 'login' }
    }
  })

  // Prefetch critical routes after initial load
  router.isReady().then(() => {
    const criticalRoutes = [
      { name: 'travel', importFn: () => import('@features/travel/pages/TravelListPage.vue') },
      { name: 'anniversaries', importFn: () => import('@features/anniversaries/pages/AnniversaryListPage.vue') },
      { name: 'notifications', importFn: () => import('@features/notifications/pages/NotificationListPage.vue') }
    ]

    const currentRouteName = router.currentRoute.value.name as string
    const routesToPrefetch = criticalRoutes.filter(r => r.name !== currentRouteName)

    prefetchCriticalRoutes(routesToPrefetch)
  })
})

// Catch unhandled promise rejections globally
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Unhandled Rejection]', event.reason)
  })
}
